import { useState } from "react";
import { toast } from "sonner";

import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { AdminAPI } from "@/lib/admin-api";
import { newIdempotencyKey } from "@/lib/idempotency";

interface Props {
  open: boolean;
  onClose: () => void;
  userId: string;
  kind: "qualification" | "wallet";
}

export function AdjustBalanceModal({ open, onClose, userId, kind }: Props) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!delta.trim() || !reason.trim()) return;
    setLoading(true);
    try {
      const key = newIdempotencyKey();
      if (kind === "qualification") {
        await AdminAPI.adjustQualification({
          user_id: userId,
          delta: Number(delta),
          reason,
          idempotency_key: key,
        });
      } else {
        await AdminAPI.adjustWallet({
          user_id: userId,
          delta,
          reason,
          idempotency_key: key,
        });
      }
      toast.success(`${kind === "qualification" ? "Points" : "Balance"} adjusted successfully`);
      setDelta(""); setReason("");
      onClose();
    } catch { /* toasted */ }
    finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose}
           title={`Adjust ${kind === "qualification" ? "Qualification Points" : "Wallet Balance"}`}>
      <div className="space-y-4">
        <div className="bg-warning-50 border border-warning-100 rounded-lg p-3 text-xs text-warning-800">
          <div className="font-semibold">Audited action</div>
          <p className="mt-1">
            This adjustment will be immutably recorded in the audit log with your admin ID.
          </p>
        </div>
        <Input
          label={`Delta (${kind === "qualification" ? "points" : "amount"})`}
          type="number" step={kind === "qualification" ? "1" : "0.01"}
          placeholder="Positive to credit, negative to debit"
          value={delta}
          onChange={(e) => setDelta(e.target.value)}
        />
        <div>
          <label className="block text-sm font-medium text-surface-800 mb-1.5">Reason</label>
          <textarea
            className="w-full h-24 px-3.5 py-2.5 bg-white border border-surface-300 rounded-lg text-sm resize-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40 focus:outline-none"
            placeholder="Explain the adjustment reason (required for audit)…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={loading} onClick={submit}
                  disabled={!delta.trim() || !reason.trim()}>
            Confirm adjustment
          </Button>
        </div>
      </div>
    </Modal>
  );
}
